<script setup lang="ts">
import Footer from "~/components/Footer.vue";
import Navbar from "~/components/Navbar.vue";
import SaveToSelectionModal from "~/components/molecules/SaveToSelectionModal.vue";
import RsvpModal from "~/components/molecules/RsvpModal.vue";
import PaymentSuccessModal from "~/components/molecules/PaymentSuccessModal.vue";
import CartSlideover from "~/components/CartSlideover.vue";
import type { Collection } from "~/server/utils";

const supabase = useSupabaseClient();

const appState = useAppState();
const route = useRoute();
const router = useRouter();
const toast = useToast();

useHead({
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ArtGallery",
        "name": "Quiet Art Movement",
        "url": "https://quietart.com",
        "logo": "https://quietart.com/logo-solid.png",
        "description": "Discover original contemporary artworks and collections from featured artists."
      })
    }
  ]
});

useSeoMeta({
  description: "Explore the Quiet Art Movement. Discover original contemporary artworks, fine art paintings, and curated collections from emerging and established featured artists.",
  ogDescription: "Explore the Quiet Art Movement. Discover original contemporary artworks, fine art paintings, and curated collections from emerging and established featured artists.",
  twitterDescription: "Explore the Quiet Art Movement. Discover original contemporary artworks, fine art paintings, and curated collections from emerging and established featured artists.",
});

onMounted(async () => {
  // Check for RSVP query params (user coming from email link)
  if (route.query.email && route.query.artworkId) {
    const email = route.query.email as string;
    const artworkId = route.query.artworkId as string;

    // Clean up query params from URL first
    const query = { ...route.query };
    delete query.email;
    delete query.artworkId;
    router.replace({ query });

    toast.add({ title: "Great! We’ll get in touch soon!", color: "success", duration: 2000 });

    // Auto-confirm RSVP (Yes) without showing modal
    try {
      await $fetch('/api/rsvp', {
        method: 'POST',
        body: {
          email,
          artworkId,
          status: true,
          name: null,
          phone: null,
        },
      });
    } catch (_) {
      // Fail silently — don't show any error to the user
    }
  }
});

watch(
  () => route.path,
  () => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }
);
</script>

<template>
  <UApp
    :toaster="{
      position: 'top-right',
    }"
    :tooltip="{
      delayDuration: 0,
      disableClosingTrigger: true,
    }"
    class="flex flex-col min-h-screen"
  >
    <Navbar />
    <div class="flex-1 flex flex-col">
      <NuxtPage />
    </div>
    <Footer />
    <ClientOnly>
      <SaveToSelectionModal />
      <RsvpModal />
      <PaymentSuccessModal />
      <CartSlideover />
    </ClientOnly>
  </UApp>
</template>

<style>

body {
  margin: 0;
  padding: 0;
  min-height: 100%;
  height: 100%;
  background-color: var(--color-primary);
  position: relative;
}

#__nuxt {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
}
</style>

<style src="./styles/root.css"></style>
